import {
  faArrowLeft,
  faArrowRight,
  faFilter,
  faFilterCircleXmark,
  faPlus,
  faRefresh,
  faSearch,
  faTable,
  faTachometer,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Scroll from "react-scroll";
import { useRecoilState } from "recoil";

import { deleteOrgUserAPI, getOrgUserListAPI } from "../../API/Org-User";
import {
  topAlertMessageState,
  topAlertStatusState,
  usersCreatedState,
  usersSearchTextState,
  usersShowFilterState,
  usersStatusState,
} from "../../AppState";
import FormErrorBox from "../../Components/Reusable/FormErrorBox";
import FormInputFieldWithButton from "../../Components/Reusable/FormInputFieldWithButton";
import FormSelectField from "../../Components/Reusable/FormSelectField";
import PageLoadingContent from "../../Components/Reusable/PageLoadingContent";
import { DEFAULT_USER_LIST_SORT_BY_VALUE } from "../../Constants/App";
import {
  USER_CREATED_LIST_OPTIONS,
  USER_STATUS_LIST_OPTIONS,
} from "../../Constants/FieldOptions";
import useLocalStorage from "../../Hooks/useLocalStorage";
import UserListDesktop from "./ListDesktop";
import UserListMobile from "./ListMobile";

//TODO: IMPLEMENT.
function OrgUserList() {
  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);
  const [showFilter, setShowFilter] = useRecoilState(usersShowFilterState);
  const [temporarySearchText, setTemporarySearchText] =
    useRecoilState(usersSearchTextState); // The search field value as your writes their query.
  const [status, setStatus] = useRecoilState(usersStatusState); // Filtering
  const [created, setCreated] = useRecoilState(usersCreatedState); // Filtering

  ////
  //// Component states.
  ////
  const [profile] = useLocalStorage("RHEA_CMS_USER_PROFILE");
  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [actualSearchText, setActualSearchText] = useState(temporarySearchText); // The actual search query value to submit to the API.
  const [sortByValue] = useState(DEFAULT_USER_LIST_SORT_BY_VALUE); // Sorting
  const [listData, setListData] = useState(""); // List Data + Pagination
  const [pageSize, setPageSize] = useState(25); // Pagination
  const [previousCursors, setPreviousCursors] = useState([]); // Pagination
  const [nextCursor, setNextCursor] = useState(""); // Pagination
  const [currentCursor, setCurrentCursor] = useState(""); // Pagination

  ////
  //// API.
  ////

  function onUserListSuccess(response) {
    setListData(response);
    if (response.hasNextPage) {
      setNextCursor(response.nextCursor); // For pagination purposes.
    }
  }

  function onUserListError(apiErr) {
    setErrors(apiErr);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onUserListDone() {
    setFetching(false);
  }

  const onClearFilterClick = (e) => {
    setShowFilter(false);
    setActualSearchText("");
    setTemporarySearchText("");
    setStatus(null);
    setCreated(368); // Last year
  };

  const onSearchButtonClick = (e) => {
    setActualSearchText(temporarySearchText);
  };

  function onOrgUserDeleteSuccess(response) {
    setTopAlertStatus("success");
    setTopAlertMessage("User deleted");
    setTimeout(() => {
      setTopAlertMessage("");
    }, 2000);

    defaultFetchList();
  }

  function onOrgUserDeleteError(apiErr) {
    setErrors(apiErr);

    setTopAlertStatus("danger");
    setTopAlertMessage("Failed deleting");
    setTimeout(() => {
      setTopAlertMessage("");
    }, 2000);

    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onOrgUserDeleteDone() {
    setFetching(false);
  }

  const defaultFetchList = () => {
    fetchList(
      actualSearchText,
      status,
      currentCursor,
      pageSize,
      created,
      sortByValue
    );
  };

  const fetchList = (keywords, status, cur, limit, cdr, sbv) => {
    // Include pagination support.
    setFetching(true);
    setErrors({});

    let params = new Map();
    if (keywords !== undefined && keywords !== null && keywords !== "") {
      params.set("search", keywords);
    }

    const statusf = status ? status : "active,invited";
    params.set("status", statusf);

    params.set("page_size", limit);

    if (cur !== "") {
      params.set("cursor", cur);
    }

    if (sbv !== undefined && sbv !== null && sbv !== "") {
      const sortArray = sbv.split(",");
      params.set("sort_field", sortArray[0]); // Sort (1 of 2)
      params.set("sort_order", sortArray[1]); // Sort (2 of 2)
    }
    if (cdr !== undefined && cdr !== null && cdr !== "") {
      let end = Math.floor(Date.now() / 1000);

      let start = end - cdr * 24 * 60 * 60;

      params.set("created_gte", start);
      params.set("created_lte", end);
    }
    if (profile?.role === "organization") {
      params.set("organizationId", profile.organizationId);
    }

    // Fetch data.
    getOrgUserListAPI(
      params,
      onUserListSuccess,
      onUserListError,
      onUserListDone
    );
  };

  const onNextClicked = (e) => {
    let arr = [...previousCursors];
    arr.push(currentCursor);
    setPreviousCursors(arr);
    setCurrentCursor(nextCursor);
  };

  const onPreviousClicked = (e) => {
    let arr = [...previousCursors];
    const previousCursor = arr.pop();
    setPreviousCursors(arr);
    setCurrentCursor(previousCursor);
  };

  const onDeleteOrgUser = (id, status) => {
    deleteOrgUserAPI(
      id,
      status,
      onOrgUserDeleteSuccess,
      onOrgUserDeleteError,
      onOrgUserDeleteDone
    );
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0);
      fetchList(
        actualSearchText,
        status,
        currentCursor,
        pageSize,
        created,
        sortByValue
      );
    }

    return () => {
      mounted = false;
    };
  }, [actualSearchText, status, currentCursor, pageSize, created, sortByValue]);

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/org-dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="is-active">
                <Link to="/org-users" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faUsers} />
                  &nbsp;Users
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/org-dashboard`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Dashboard
                </Link>
              </li>
            </ul>
          </nav>

          {/* Model(s) */}
          {/* None in this page */}

          {/* Content */}
          <nav class="box">
            <div class="columns">
              <div class="column">
                <h1 class="title is-4">
                  <FontAwesomeIcon className="fas" icon={faUsers} />
                  &nbsp;Users
                </h1>
              </div>
              {!isFetching && (
                <div class="column has-text-right">
                  <button
                    class="button is-small is-info is-fullwidth-mobile"
                    onClick={defaultFetchList}
                  >
                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                    <span class="is-hidden-desktop is-hidden-tablet">
                      &nbsp;Refresh
                    </span>
                  </button>
                  &nbsp;
                  <button
                    onClick={(e, i) => {
                      setShowFilter(!showFilter);
                    }}
                    class="button is-small is-link is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faFilter} />
                    &nbsp;Filter
                  </button>
                  &nbsp;
                  <Link
                    to={`/org-user/add`}
                    class="button is-small is-success is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faPlus} />
                    &nbsp;Add User
                  </Link>
                </div>
              )}
            </div>

            {/* Filter Box */}
            {showFilter && (
              <>
                <div
                  class="has-background-white-bis"
                  style={{ borderRadius: "15px", padding: "20px" }}
                >
                  {/* Filter Title + Clear Button */}
                  <div class="columns">
                    <div class="column is-half">
                      <strong>
                        <u>
                          <FontAwesomeIcon className="mdi" icon={faFilter} />
                          &nbsp;Filter
                        </u>
                      </strong>
                    </div>
                    <div class="column is-half has-text-right">
                      <Link onClick={onClearFilterClick}>
                        <FontAwesomeIcon
                          className="mdi"
                          icon={faFilterCircleXmark}
                        />
                        &nbsp;Clear Filter
                      </Link>
                    </div>
                  </div>

                  {/* Filter Options */}
                  <div class="columns">
                    <div class="column is-half">
                      <FormInputFieldWithButton
                        label={"Search"}
                        name="temporarySearchText"
                        type="text"
                        placeholder="Search by name, email, clinical code, etc"
                        value={temporarySearchText}
                        helpText=""
                        onChange={(e) => setTemporarySearchText(e.target.value)}
                        isRequired={true}
                        maxWidth="100%"
                        buttonLabel={
                          <>
                            <FontAwesomeIcon className="fas" icon={faSearch} />
                          </>
                        }
                        onButtonClick={onSearchButtonClick}
                      />
                    </div>
                    <div class="column">
                      <div class="">
                        <FormSelectField
                          label="Trial Status"
                          name="Trial Status"
                          placeholder="Text input"
                          selectedValue={status}
                          options={USER_STATUS_LIST_OPTIONS}
                          errorText={errors && errors.status}
                          helpText=""
                          onChange={(e) => setStatus(e.target.value)}
                          maxWidth="150px"
                        />
                      </div>
                    </div>
                    <div class="column">
                      <FormSelectField
                        label="Created"
                        name="created"
                        placeholder="Text input"
                        selectedValue={parseInt(created)}
                        options={USER_CREATED_LIST_OPTIONS}
                        errorText={errors && errors.created}
                        helpText=""
                        onChange={(e) => setCreated(parseInt(e.target.value))}
                        maxWidth="150px"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {isFetching ? (
              <>
                <PageLoadingContent displayMessage={"Please wait"} />
              </>
            ) : (
              <>
                <FormErrorBox errors={errors} />
                {listData &&
                (listData.length > 0 || previousCursors.length > 0) ? (
                  <div class="container">
                    {/*
                                            ##################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A DESKTOP SCREEN.
                                            ##################################################################
                                        */}
                    <div class="is-hidden-touch">
                      <UserListDesktop
                        listData={listData}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        previousCursors={previousCursors}
                        onPreviousClicked={onPreviousClicked}
                        onNextClicked={onNextClicked}
                        onDeleteOrgUser={onDeleteOrgUser}
                      />
                    </div>

                    {/*
                                            ###########################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A TABLET OR MOBILE SCREEN.
                                            ###########################################################################
                                        */}
                    <div class="is-fullwidth is-hidden-desktop">
                      <UserListMobile
                        listData={listData}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        previousCursors={previousCursors}
                        onPreviousClicked={onPreviousClicked}
                        onNextClicked={onNextClicked}
                      />
                    </div>
                  </div>
                ) : (
                  <div class="hero-body">
                    <p class="title">
                      <FontAwesomeIcon className="fas" icon={faTable} />
                      &nbsp;No data
                    </p>
                    <p class="subtitle">
                      No data.{" "}
                      <b>
                        <Link to="/users/add">
                          Click here&nbsp;
                          <FontAwesomeIcon
                            className="mdi"
                            icon={faArrowRight}
                          />
                        </Link>
                      </b>{" "}
                      to get started creating your first user.
                    </p>
                  </div>
                )}
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default OrgUserList;
